import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { InstagramLogo, FacebookLogo, YoutubeLogo, LinkedinLogo, XLogo } from "@phosphor-icons/react";

const NAV = [
  { to: "/",           label: "Home",             end: true },
  { to: "/projects",   label: "Portfolio" },
  { to: "/properties", label: "Properties" },
  { to: "/builders",   label: "Builder's Corner" },
  { to: "/blog",       label: "Blog" },
  { to: "/about",      label: "About Us" },
];

/**
 * Sticky, transparent-on-hero, shrinks gracefully on scroll.
 * Applies the `.premium-marketing` class to its root so ALL descendants
 * inherit the editorial palette + typography.
 */
export default function MarketingLayout({ children, hero = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const transparent = hero && !scrolled;

  return (
    <div className="premium-marketing">
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          transparent ? "bg-transparent" : "bg-[#0A0A0A]/85 backdrop-blur-md border-b border-[#1F1F1F]"
        }`}
        data-testid="premium-marketing-header"
      >
        <div className={`max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 transition-all ${scrolled ? "py-3" : "py-5"}`}>
          <Link to="/" data-testid="premium-brand" className="flex items-center gap-3 group">
            <span className="serif text-[22px] md:text-[26px] cream leading-none tracking-tight">
              Dream Home <span className="gold">Planners</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.end}
                data-testid={`premium-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[15px] tracking-wide transition-colors ${isActive ? "gold" : "cream/70 hover:text-[#C9A96E]"}`}
                style={({ isActive }) => ({ color: isActive ? "#C9A96E" : "rgba(245,241,234,.8)" })}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="premium-signin"
                  className="hidden sm:inline-block text-[14px] tracking-wide bg-[#C9A96E] text-[#0A0A0A] px-5 py-2 border border-[#C9A96E] hover:bg-[#DFBB7C] hover:border-[#DFBB7C] transition-colors font-semibold shadow-[0_0_16px_rgba(201,169,110,0.35)]">
              Studio Login →
            </Link>
            <button
              className="md:hidden text-[#F5F1EA] mono p-2 border border-[#2A2A2A]"
              onClick={() => setOpen((v) => !v)}
              data-testid="premium-nav-toggle"
              aria-label="Open menu"
            >
              {open ? "×" : "☰"}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-[#1F1F1F] bg-[#0A0A0A]">
            <div className="flex flex-col divide-y divide-[#1F1F1F]">
              {NAV.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  className="text-[16px] tracking-wide px-6 py-4 text-[#F5F1EA]/85 hover:text-[#C9A96E]">
                  {n.label}
                </NavLink>
              ))}
              <Link to="/login" className="text-[16px] tracking-wide px-6 py-4 bg-[#C9A96E] text-[#0A0A0A] font-semibold">Studio Login →</Link>
            </div>
          </div>
        )}
      </header>
      <main className={hero ? "" : "pt-24"}>{children}</main>
      <PremiumFooter />
    </div>
  );
}

export function PremiumFooter() {
  return (
    <footer className="border-t border-[#1F1F1F] mt-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="serif text-3xl md:text-4xl leading-tight">
            Draft the life you&apos;d like <span className="gold italic">to live in.</span>
          </div>
          <p className="mt-5 text-[#B8B0A0] text-sm max-w-md leading-relaxed">
            Dream Home Planners — an architecture &amp; interior studio building thoughtful,
            enduring residences and turnkey commercial spaces since 2014.
          </p>
          <Link to="/contact" data-testid="premium-footer-book"
                className="inline-block mt-8 mono border border-[#C9A96E] gold px-6 py-3 hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors">
            Book a Design Session →
          </Link>
        </div>
        <div>
          <div className="mono text-[#7A7267] mb-4">Studio</div>
          <ul className="space-y-2 text-sm text-[#DDD5C4]">
            {NAV.map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-[#C9A96E]">{n.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="mono text-[#7A7267] mb-4">Reach us</div>
          <ul className="space-y-2 text-sm text-[#DDD5C4]">
            <li>Studio DHP, MG Road</li>
            <li>Bengaluru, KA 560001</li>
            <li>+91 90000 00000</li>
            <li><a href="mailto:hello@dreamhomeplanners.com" className="gold-link">hello@dreamhomeplanners.com</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#1F1F1F]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between gap-4 text-xs text-[#7A7267]">
          <div>© {new Date().getFullYear()} Dream Home Planners</div>
          <SocialLinks />
          <div>Architecture · Interior · Turnkey</div>
        </div>
      </div>
    </footer>
  );
}

const SOCIALS = [
  { Icon: InstagramLogo, url: "https://instagram.com/dreamhomeplanners", label: "Instagram" },
  { Icon: FacebookLogo,  url: "https://facebook.com/dreamhomeplanners",  label: "Facebook" },
  { Icon: YoutubeLogo,   url: "https://youtube.com/@dreamhomeplanners",  label: "YouTube" },
  { Icon: LinkedinLogo,  url: "https://linkedin.com/company/dreamhomeplanners", label: "LinkedIn" },
  { Icon: XLogo,         url: "https://x.com/dreamhomeplanner", label: "X" },
];

function SocialLinks() {
  return (
    <div className="flex items-center gap-3" data-testid="footer-socials">
      {SOCIALS.map(({ Icon, url, label }) => (
        <a key={label} href={url} target="_blank" rel="noreferrer"
           data-testid={`social-${label.toLowerCase()}`}
           aria-label={label}
           className="w-9 h-9 flex items-center justify-center border border-[#2A2A2A] text-[#B8B0A0] hover:text-[#C9A96E] hover:border-[#C9A96E] transition-colors">
          <Icon size={16} weight="regular" />
        </a>
      ))}
    </div>
  );
}
